/**
 * Header Component
 * Top navigation bar with LensLock branding, user info and global actions
 */
import React from "react";
import {
  makeStyles,
  tokens,
  Text,
  Avatar,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Button,
  Divider,
} from "@fluentui/react-components";
import {
  PersonCircle24Regular,
  SignOut24Regular,
  Settings24Regular,
} from "@fluentui/react-icons";

import { useMsal } from "@azure/msal-react";

import { usePermissions } from "../../auth/usePermissions";
import { AppRoles } from "../../auth/authConfig";
import { useDevBypass, devAccount } from "../../utils/devBypass";

const useStyles = makeStyles({
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: "60px",
    padding: `0 ${tokens.spacingHorizontalXL}`,
    background: "linear-gradient(135deg, #004C97 0%, #003a75 100%)",
    color: "#ffffff",
    boxShadow: "0 2px 8px rgba(0, 76, 151, 0.25)",
    zIndex: 100,
  },
  logo: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalL,
  },
  logoContainer: {
    backgroundColor: "#ffffff",
    padding: "2px 3px",
    borderRadius: "0px",
    display: "flex",
    alignItems: "center",
  },
  logoImage: {
    height: "44px",
    width: "auto",
  },
  logoTextContainer: {
    display: "flex",
    flexDirection: "column",
  },
  logoText: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#ffffff",
    letterSpacing: "0.5px",
    fontFamily: "'Poppins', sans-serif",
  },
  logoSubtext: {
    fontSize: "11px",
    color: "rgba(255, 255, 255, 0.7)",
    letterSpacing: "0.3px",
    marginTop: "-2px",
  },
  userSection: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
  },
  userInfo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    marginRight: tokens.spacingHorizontalS,
  },
  userName: {
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: 500,
  },
  userEmail: {
    color: "rgba(255, 255, 255, 0.7)",
    fontSize: "12px",
  },
  avatarButton: {
    cursor: "pointer",
    borderRadius: "50%",
    border: "2px solid rgba(255, 255, 255, 0.3)",
  },
});

export const Header: React.FC = () => {
  const styles = useStyles();
  const { hasRole } = usePermissions();
  const { accounts } = useMsal();

  // Get user info from MSAL account or dev bypass
  const account = useDevBypass ? devAccount : accounts[0];
  const userName = account?.name || account?.username?.split("@")[0] || "User";
  const userEmail = account?.username || "";
  const userInitials = userName
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "U";

  const handleLogout = () => {
    if (useDevBypass) {
      // In dev mode, just reload
      window.location.href = "/";
      return;
    }
    // Normal logout - clear session
    sessionStorage.clear();
    window.location.href = "/";
  };

  return (
    <header className={styles.header}>
      <div className={styles.logo}>
        <div className={styles.logoContainer}>
          <img 
            src="/photon_icon_2_rectangular.png" 
            alt="Photon AI" 
            className={styles.logoImage}
          />
        </div>
        <div className={styles.logoTextContainer}>
          <Text className={styles.logoText}>LensLock Photon AI Portal</Text>
          <Text className={styles.logoSubtext}>Confidential - For Authorized Use Only</Text>
        </div>
      </div>

      <div className={styles.userSection}>
        <div className={styles.userInfo}>
          <Text className={styles.userName}>{userName}</Text>
          <Text className={styles.userEmail}>{userEmail}</Text>
        </div>

        <Menu>
          <MenuTrigger>
            <Button
              appearance="transparent"
              icon={
                <Avatar 
                  name={userName} 
                  initials={userInitials} 
                  size={36}
                  color="brand"
                />
              }
              className={styles.avatarButton}
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem icon={<PersonCircle24Regular />}>My Profile</MenuItem>
              {hasRole(AppRoles.PORTAL_ADMIN) && (
                <>
                  <Divider />
                  <MenuItem
                    icon={<Settings24Regular />}
                    onClick={() => (window.location.href = "/admin")}
                  >
                    Admin Settings
                  </MenuItem>
                </>
              )}
              <Divider />
              <MenuItem icon={<SignOut24Regular />} onClick={handleLogout}>
                Sign Out
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>
    </header>
  );
};

export default Header;
